import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { Body2, Body3, Tiny1, Title2, Title4 } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { parseUnits } from 'utils/units';
import { formatUnits } from 'viem';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createWithdrawValidation } from 'structure/pv2/vault/utils/createValidationSchema';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { HorizontalLine } from '@dapp/multi-lib/sdaoui/HorizontalLine';
import { ModalDetailsRow } from 'structure/pv2/vault/components/ModalDetailsRow';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import { InputTokenAmount } from 'components/form/InputTokenAmount';
import { Collapse } from '@dapp/multi-lib/sdaoui/Collapse';
import { SlippageInput } from 'structure/pv2/vault/components/SlippageInput';
import { useVault } from 'structure/pv2/vault/hooks/useVault';
import {
  calcMinAmountOut,
  VaultIdentity,
  getVaultZapperAddress,
  getVaultWithdrawalFee,
} from 'structure/pv2/vault/utils/vault';
import {
  formatPercent,
  formatTokenAmount,
  formatValueToSixDecimals,
} from '@dapp/multi-lib/lib/formatters';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useConnection } from 'wagmi';
import {
  usePreviewSwap,
  useSpenderAllowance,
  useSwap,
} from 'structure/pv2/vault/hooks/useDynaRouterContract';
import { ethers } from 'ethers';
import { waitForTransaction } from 'utils/waitForTransaction';
import { DynaVault, Token } from '../../../../api/DynaVaultsAPI';
import { useApprove } from 'utils/hooks/useERC20Contract';
import { getDepositTokenList } from 'structure/pv2/vault/utils/depositTokenList';
import { useDebounce } from 'use-debounce';
import { getTokenIconUrl } from 'utils/url';
import { useSwitchNetwork } from 'utils/hooks/useSwitchNetwork';
import { SpendLimitInput } from '../components/SpendLimitInput';
import { TokenImage } from 'components/TokenImage';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useTokenBalance } from 'utils/hooks/useTokenBalance';
import { useTokenPrice } from 'structure/pv2/vault/hooks/useTokenPrice';

interface FormData {
  tokenAmount?: number;
  slippage?: number;
  spendingLimit?: string;
}

interface ModalVaultWithdrawProps {
  open: boolean;
  vaultIdentity: VaultIdentity;
  onOpenChange: (open: boolean) => void;
}

const ONE_MINUTE = 60;

export const ModalVaultWithdraw = (props: ModalVaultWithdrawProps) => {
  const { t } = useTranslation('vault');
  const { vaultIdentity, open, onOpenChange } = props;
  const vaultQuery = useVault(vaultIdentity);

  return (
    <ModalV2.Root modal={true} open={open} onOpenChange={onOpenChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={false}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <ModalV2.BodyTitle
          variant='dark'
          endEnhancer={<ModalV2.Close variant='dark' />}
        >
          <Title2>{t('MODAL_WITHDRAW.TITLE')}</Title2>
        </ModalV2.BodyTitle>
        <HorizontalLine className='my-4 h-[0.2px] border-white-300' />

        {vaultQuery.isLoading && (
          <ModalV2.Body variant='dark'>
            <Body2>Loading...</Body2>
          </ModalV2.Body>
        )}

        {vaultQuery.data && (
          <ModalContent vault={vaultQuery.data} onOpenChange={onOpenChange} />
        )}
      </ModalV2.Content>
    </ModalV2.Root>
  );
};

interface ModalContentProps {
  onOpenChange: (open: boolean) => void;
  vault: DynaVault;
}

const ModalContent = (props: ModalContentProps) => {
  const { onOpenChange } = props;
  const { t } = useTranslation('vault');
  const toast = useToast();
  const queryClient = useQueryClient();
  const { vault } = props;

  const depositTokenList = getDepositTokenList(vault.chainId);
  const depositToken = depositTokenList.find(
    (item) => item.address === vault.depositToken
  );

  const [withdrawToken] = useState<Token>(depositToken || depositTokenList[0]);

  const account = useConnection();
  const vaultChainId = vault.chainId;
  const tokenOut = withdrawToken.address;
  const vaultTokenAddress = vault.address as `0x${string}`;
  const vaultZapperAddress = getVaultZapperAddress(vault);

  const tokenInBalanceQuery = useTokenBalance({
    address: account.address,
    token: vaultTokenAddress,
    chainId: vaultChainId,
    query: {
      enabled: Boolean(account.address),
    },
  });

  const tokenInBalance = tokenInBalanceQuery.data?.value || BigInt(0);
  const tokenInDecimals = vault.decimals;

  const [slippageOpen, setSlippageOpen] = useState(false);
  const [spendLimitOpen, setSpendLimitOpen] = useState(false);

  const toggleSpendLimit = () => setSpendLimitOpen((open) => !open);

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const vaultTokenPriceQuery = useTokenPrice({
    tokenAddress: vault.address,
    chainId: vaultChainId,
  });

  const vaultTokenPrice = vaultTokenPriceQuery.data
    ? vaultTokenPriceQuery.data.formatedPrice
    : '0';

  const schema = createWithdrawValidation({
    tokenAmount: {
      label: t('FORM.TOKEN_AMOUNT'),
      max: Number(formatUnits(tokenInBalance, tokenInDecimals)),
      required: true,
    },
  });

  const form = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { slippage: 0.5, spendingLimit: '0' },
  });

  const handleChangeSlippage = (value: number) => {
    form.setValue('slippage', value);
    form.trigger('slippage');
  };

  const handleClickMax = () => {
    const maxAmount = formatUnits(tokenInBalance, tokenInDecimals);
    form.setValue('tokenAmount', maxAmount as unknown as number);
    form.trigger('tokenAmount');
  };

  const tokenAmount = form.watch('tokenAmount') || '0';
  const usdAmount = Number(tokenAmount) * Number(vaultTokenPrice);
  const slippage = form.watch('slippage');
  const customSpendLimit = form.watch('spendingLimit');

  const [bnTokenInAmount] = useDebounce(
    parseUnits(tokenAmount, tokenInDecimals),
    400,
    {
      equalityFn: (prev, next) => prev.eq(next),
    }
  );

  const [spendingLimit, { isPending }] = useDebounce(
    parseUnits(
      Number(customSpendLimit) > 0
        ? customSpendLimit ?? '0'
        : tokenAmount?.toString() || '0',
      tokenInDecimals
    ),
    400,
    { equalityFn: (prev, next) => prev.eq(next) }
  );

  const { chain } = useConnection();
  const isCorrectChain = chain?.id === vaultChainId;

  const previewSwap = usePreviewSwap({
    from: (account.address ?? '0x0') as `0x${string}`,
    contractAddress: vaultZapperAddress as `0x${string}`,
    chainId: vaultChainId,
    tokenIn: vaultTokenAddress,
    amountIn: bnTokenInAmount.toString(),
    tokenOut: tokenOut,
    query: {
      enabled: bnTokenInAmount.gt(0) && isCorrectChain,
    },
  });

  const spenderAllowance = useSpenderAllowance({
    contractAddress: vaultZapperAddress as `0x${string}`,
    account: (account.address ?? '0x0') as `0x${string}`,
    chainId: vaultChainId,
    tokenAddress: vaultTokenAddress,
    query: {
      enabled: true,
      staleTime: ONE_MINUTE,
    },
  });

  const approve = useApprove({
    chainId: vaultChainId,
    tokenAmount: spendingLimit.toString(),
    tokenAddress: vaultTokenAddress,
    spenderAddress: vaultZapperAddress,
    query: {
      enabled: true,
    },
  });

  const bnAllowanceAmount = ethers.BigNumber.from(spenderAllowance.data || 0);

  const requiredAllowance = bnTokenInAmount.gt(bnAllowanceAmount);

  const minAmountOut = calcMinAmountOut(
    previewSwap.data?.amountOut || '0',
    slippage ?? 0
  );

  const swap = useSwap({
    contractAddress: vaultZapperAddress as `0x${string}`,
    chainId: vaultChainId,
    tokenIn: vaultTokenAddress,
    amountIn: bnTokenInAmount.toString(),
    tokenOut: tokenOut,
    minAmountOut: minAmountOut.toString(),
    routerAddress: (previewSwap.data?.selectedRouter ?? '0x0') as `0x${string}`,
    from: (account.address ?? '0x0') as `0x${string}`,
    to: (account.address ?? '0x0') as `0x${string}`,
    swapData: previewSwap.data?.swapData ?? [],
    query: {
      enabled:
        bnTokenInAmount.gt(0) &&
        !!previewSwap.data?.swapData &&
        !!previewSwap.data?.selectedRouter &&
        !requiredAllowance &&
        isCorrectChain,
    },
  });

  const handleWithdraw = async () => {
    if (swap.prepare.isError) {
      console.log('### swap.prepare.error', swap.prepare);
      toast({
        title: t('TOAST.FAILED_TO_REQUEST_WITHDRAW_TITLE'),
        description: swap.prepare.error.message,
        type: 'error',
      });
      return;
    }

    try {
      const result = await swap.contract.writeContractAsync(
        swap.prepare.data!.request
      );

      await waitForTransaction({
        chainId: vaultChainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toast({
        title: t('TOAST.WITHDRAW_SUCCESS_TITLE'),
        description: `Code: ${result}`,
        type: 'success',
      });

      tokenInBalanceQuery.refetch();

      queryClient.invalidateQueries({
        queryKey: queryKeys.vaultScope,
      });

      handleOpenChange(false);
    } catch (error) {
      console.log('### withdraw error', error);

      const errorMessage = getTxErrorMessage(
        error as Error,
        t('TOAST.FAILED_TO_REQUEST_WITHDRAW_DESC')
      );

      toast({
        title: t('TOAST.FAILED_TO_REQUEST_WITHDRAW_TITLE'),
        description: errorMessage,
        type: 'error',
      });
    }
  };

  const handleApprove = async () => {
    if (approve.prepare.isError) {
      toast({
        title: t('TOAST.FAILED_TO_REQUEST_APPROVE_TITLE'),
        description: approve.prepare.error.message,
        type: 'error',
      });
      return;
    }

    try {
      const result = await approve.contract.writeContractAsync(
        approve.prepare.data!.request
      );

      await waitForTransaction({
        chainId: vaultChainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toast({
        title: t('TOAST.APPROVE_SUCCESS_TITLE'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await spenderAllowance.refetch();
    } catch (error) {
      console.log('### approve error', error);

      const errorMessage = getTxErrorMessage(
        error as Error,
        t('TOAST.FAILED_TO_REQUEST_APPROVE_DESC')
      );

      toast({
        title: t('TOAST.FAILED_TO_REQUEST_APPROVE_TITLE'),
        description: errorMessage,
        type: 'error',
      });
    }
  };

  const { switchNetworkAsync } = useSwitchNetwork();

  useEffect(() => {
    switchNetworkAsync({ chainId: vaultChainId });
  }, []);

  const handleSwitchChain = () => {
    return switchNetworkAsync({ chainId: vaultChainId });
  };

  const handleSubmit = async () => {
    if (!isCorrectChain) {
      return switchNetworkAsync({ chainId: vaultChainId });
    }

    if (requiredAllowance) {
      return await handleApprove();
    }
    return handleWithdraw();
  };

  if (previewSwap.error) {
    console.log('### previewSwap', previewSwap);
  }

  const zeroAmountOut = previewSwap.data && previewSwap.data.amountOut <= 0;

  const isApproveLoading =
    isPending() ||
    approve.contract.isPending ||
    approve.prepare.isLoading ||
    form.formState.isSubmitting;

  const isApproveDisabled =
    isApproveLoading || approve.prepare.isError || approve.contract.isPending;

  const isDepositLoading =
    swap.contract.isPending ||
    swap.prepare.isLoading ||
    form.formState.isSubmitting ||
    previewSwap.isLoading ||
    swap.gasQuery.isLoading ||
    isPending();

  const isWithdrawDisabled =
    isDepositLoading || swap.prepare.isError || zeroAmountOut;

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <ModalV2.Body variant='dark'>
        <div className='flex flex-row items-center justify-end gap-1 pb-1'>
          <Body3 className='text-white-500'>
            {t('MODAL_WITHDRAW.MAX_WITHDRAW')}:
          </Body3>
          <Body2>{formatTokenAmount(tokenInBalance, tokenInDecimals)}</Body2>
          <TokenImage
            height={16}
            width={16}
            symbol={vault.symbol}
            src={getTokenIconUrl(vault.symbol)}
            alt={withdrawToken.symbol}
          />
          <div className='cursor-pointer' onClick={handleClickMax}>
            <Button style='text' className='px-0 py-0'>
              MAX
            </Button>
          </div>
        </div>
        <InputTokenAmount
          tokenSymbol={vault.symbol}
          control={form.control}
          error={form.formState.errors.tokenAmount?.message}
          endEnhancer={
            <span className='text-sm font-normal text-white-500'>
              ${formatValueToSixDecimals(usdAmount)}
            </span>
          }
          {...form.register('tokenAmount')}
        />

        <div className='mt-8 flex flex-col gap-3'>
          <Title4 className='pb-1 text-[1rem]'>{t('DETAILS.DETAILS')}</Title4>

          <div className='flex flex-col gap-1'>
            <ModalDetailsRow
              label={t('DETAILS.SLIPPAGE_TOLERANCE')}
              value={
                <div className='flex flex-row items-center gap-1'>
                  <div>{form.getValues('slippage')}%</div>
                  <ButtonIcon
                    className='px-0 py-0'
                    onClick={() => setSlippageOpen((open) => !open)}
                  >
                    <Icon
                      icon='settings'
                      width={16}
                      height={16}
                      color='#D9D9D9'
                    />
                  </ButtonIcon>
                </div>
              }
            />
            <Collapse visible={slippageOpen} type={'vertical'}>
              <SlippageInput
                onChangeSlippage={handleChangeSlippage}
                value={form.getValues('slippage')}
                {...form.register('slippage')}
              />
            </Collapse>
          </div>
          {isCorrectChain && requiredAllowance && (
            <div className='flex flex-col gap-1'>
              <ModalDetailsRow
                label={t('DETAILS.SPEND_LIMIT_PERMISSION')}
                value={
                  <div className='flex flex-row items-center gap-1'>
                    <div>{tokenAmount}</div>
                    <ButtonIcon
                      className='px-0 py-0'
                      onClick={toggleSpendLimit}
                    >
                      <Icon
                        icon={spendLimitOpen ? 'arrow_drop_up' : 'edit'}
                        width={16}
                        height={16}
                        color='#D9D9D9'
                      />
                    </ButtonIcon>
                  </div>
                }
              />
              <Collapse visible={spendLimitOpen} type={'vertical'}>
                <SpendLimitInput
                  defaultSpendLimit={tokenAmount.toString()}
                  customSpendLimit={
                    Number(customSpendLimit) > 0
                      ? customSpendLimit ?? '0'
                      : tokenAmount.toString()
                  }
                  onSpendLimitChange={(value) =>
                    form.setValue('spendingLimit', value)
                  }
                />
              </Collapse>
            </div>
          )}
          <ModalDetailsRow
            label={t('DETAILS.MINIMUM_AMOUNT_OUT')}
            value={
              <div className='flex flex-row items-center gap-1'>
                <div>
                  {ethers.utils.formatUnits(
                    minAmountOut.toString() || 0,
                    withdrawToken.decimals
                  )}
                </div>
                <TokenImage
                  symbol={withdrawToken.symbol}
                  src={getTokenIconUrl(withdrawToken.symbol)}
                  alt={withdrawToken.symbol}
                  width={16}
                  height={16}
                />
              </div>
            }
          />
          <ModalDetailsRow
            label={t('DETAILS.EXPECTED_AMOUNT_OUT')}
            value={
              <div className='flex flex-col items-end gap-1'>
                <div className='flex flex-row items-center gap-1'>
                  {ethers.utils.formatUnits(
                    previewSwap.data?.amountOut || '0',
                    withdrawToken.decimals
                  )}
                  <TokenImage
                    symbol={withdrawToken.symbol}
                    src={getTokenIconUrl(withdrawToken.symbol)}
                    alt={withdrawToken.symbol}
                    width={16}
                    height={16}
                  />
                </div>
                <div className='flex flex-row items-center gap-1'>
                  <Tiny1 className='text-white-400'>
                    {tokenAmount} {t('DETAILS.SHARES')}
                  </Tiny1>
                </div>
              </div>
            }
          />
          <HorizontalLine />
          <ModalDetailsRow
            label={t('DETAILS.WITHDRAWAL_FEE_DEDUCTED')}
            value={
              <div className='flex flex-row items-center gap-1'>
                {formatPercent(getVaultWithdrawalFee(vault))}%
              </div>
            }
          />
        </div>
      </ModalV2.Body>
      <div className='flex flex-col py-2'>
        {previewSwap.error && (
          <Body2 className='text-red-600'>
            {getTxErrorMessage(
              previewSwap.error,
              'Error occurred while fetching swap data'
            )}
          </Body2>
        )}

        {swap.prepare.error && (
          <Body2 className='text-red-600'>
            {getTxErrorMessage(
              swap.prepare.error,
              'Error occurred while fetching swap data'
            )}
          </Body2>
        )}

        {zeroAmountOut && (
          <Body2 className='text-red-600'>
            {t('MODAL_WITHDRAW.ERROR_ZERO_AMOUNT_OUT')}
          </Body2>
        )}
      </div>
      <div className='mt-8 flex justify-end gap-4'>
        <Button style='outline' onClick={() => onOpenChange(false)}>
          {t('MODAL_WITHDRAW.BUTTON_CANCEL')}
        </Button>
        {!isCorrectChain ? (
          <Button type='button' onClick={handleSwitchChain}>
            {t('MODAL_WITHDRAW.BUTTON_SWITCH_NETWORK')}
          </Button>
        ) : null}

        {isCorrectChain && requiredAllowance ? (
          <Button
            loading={isApproveLoading}
            disabled={isApproveDisabled}
            type='submit'
          >
            {t('MODAL_WITHDRAW.BUTTON_APPROVE')}
          </Button>
        ) : null}
        {isCorrectChain && !requiredAllowance ? (
          <Button
            loading={isDepositLoading}
            disabled={isWithdrawDisabled}
            type='submit'
          >
            {t('MODAL_WITHDRAW.BUTTON_WITHDRAW')}
          </Button>
        ) : null}
      </div>
    </form>
  );
};
